import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api, handleApiError } from '../lib/api';
import { Wallet as WalletIcon, Loader2, CheckCircle2, ArrowLeft } from 'lucide-react';
import { ApiResponse, PaginatedResponse, Transaction, WalletStats, PaymentMethod } from '@ouagaliv/shared';

const MIN_RECHARGE = 500;
const MAX_RECHARGE = 1000000;
// Estimation affichée avant validation. Le montant exact est calculé par le serveur.
const COMMISSION_ESTIMATE = 0.15;
const QUICK_AMOUNTS = [1000, 2000, 5000, 10000, 20000];

type RechargeResult = {
  transaction: Transaction;
  commissionFcfa: number;
  creditedFcfa: number;
  commissionRate: number;
};

const fcfa = (n: number) => `${n.toLocaleString('fr-FR')} FCFA`;
const formatDate = (date: string) =>
  new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });

export default function Recharge() {
  const [balance, setBalance] = useState(0);
  const [recharges, setRecharges] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState<{ credited: number; commission: number; balance: number } | null>(null);

  const load = async () => {
    try {
      const [s, t] = await Promise.all([
        api.get<ApiResponse<WalletStats>>('/wallet/stats'),
        api.get<ApiResponse<PaginatedResponse<Transaction>>>('/wallet/transactions', { params: { limit: 50 } }),
      ]);
      if (s.data.success && s.data.data) setBalance(s.data.data.currentBalance);
      if (t.data.success && t.data.data) {
        setRecharges(
          t.data.data.items
            .filter(tx => tx.type === 'credit' && (tx.metadata as any)?.rechargeAmount)
            .slice(0, 5)
        );
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const value = Number(amount);
  const isValid = Number.isInteger(value) && value >= MIN_RECHARGE && value <= MAX_RECHARGE;
  const estimatedCommission = isValid ? Math.round(value * COMMISSION_ESTIMATE) : 0;
  const estimatedCredit = isValid ? value - estimatedCommission : 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || submitting) return;
    if (!window.confirm(`Confirmer la recharge de ${fcfa(value)} ?`)) return;
    setError('');
    setSuccess(null);
    setSubmitting(true);
    try {
      const res = await api.post<ApiResponse<RechargeResult>>('/wallet/recharge', {
        amountFcfa: value,
        paymentMethod: PaymentMethod.MOBILE_MONEY,
      });
      if (!res.data.success) throw new Error(res.data.error?.message || 'Recharge impossible');
      if (!res.data.data) throw new Error('Recharge impossible');
      const { creditedFcfa, commissionFcfa, transaction } = res.data.data;
      setSuccess({ credited: creditedFcfa, commission: commissionFcfa, balance: transaction.balanceAfterFcfa });
      setBalance(transaction.balanceAfterFcfa);
      setAmount('');
      load();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 size={48} className="text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container pt-4 pb-24">
        <div className="flex items-center gap-3 mb-6">
          <Link to="/earnings" aria-label="Retour aux gains" className="p-2 rounded-lg hover:bg-gray-100 text-text">
            <ArrowLeft size={22} />
          </Link>
          <h1 className="text-xl font-semibold text-text">Recharger mon crédit</h1>
        </div>

        {error && (
          <div className="card mb-4" style={{ borderColor: 'var(--color-error)' }}>
            <span style={{ color: 'var(--color-error)', fontSize: 14 }}>{error}</span>
          </div>
        )}

        {success && (
          <div className="card mb-4" style={{ borderColor: '#059669' }}>
            <div className="flex items-start gap-3">
              <CheckCircle2 size={24} style={{ color: '#059669', flexShrink: 0 }} />
              <div style={{ fontSize: 14 }}>
                <p className="font-semibold text-text">Recharge réussie</p>
                <p className="text-text-secondary">
                  {fcfa(success.credited)} ajoutés à votre solde (commission : {fcfa(success.commission)}).
                </p>
                <p className="text-text-secondary">Nouveau solde : {fcfa(success.balance)}</p>
              </div>
            </div>
          </div>
        )}

        <div className="card mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center text-green-600">
              <WalletIcon size={24} />
            </div>
            <div>
              <p className="text-sm text-text-secondary">Solde actuel</p>
              <p className="text-xl font-bold text-text">{fcfa(balance)}</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="card space-y-4 mb-4">
          <div>
            <p className="font-medium text-text mb-2">Choisissez un montant</p>
            <div className="grid grid-cols-3 gap-2">
              {QUICK_AMOUNTS.map(q => (
                <button
                  key={q}
                  type="button"
                  onClick={() => setAmount(String(q))}
                  className={`py-2 rounded-lg text-sm font-medium border ${
                    value === q ? 'bg-primary text-white border-primary' : 'bg-white text-text border-border'
                  }`}
                >
                  {q.toLocaleString('fr-FR')}
                </button>
              ))}
            </div>
          </div>

          <div className="input-group">
            <label className="input-label" htmlFor="recharge-amount">Ou tapez un autre montant (FCFA)</label>
            <input
              id="recharge-amount"
              type="number"
              inputMode="numeric"
              className="input-field"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder="Ex : 3000"
              min={MIN_RECHARGE}
              max={MAX_RECHARGE}
              step={1}
            />
            <p className="input-hint">Minimum {fcfa(MIN_RECHARGE)}</p>
          </div>

          {amount !== '' && !isValid && (
            <p style={{ color: 'var(--color-error)', fontSize: 14 }}>
              Le montant doit être un nombre entier entre {fcfa(MIN_RECHARGE)} et {fcfa(MAX_RECHARGE)}.
            </p>
          )}

          {isValid && (
            <div className="p-4 bg-green-50 rounded-lg space-y-1" style={{ fontSize: 14 }}>
              <div className="flex justify-between">
                <span className="text-text-secondary">Vous rechargez</span>
                <span className="font-medium text-text">{fcfa(value)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-secondary">Commission (environ 15 %)</span>
                <span className="font-medium text-text">- {fcfa(estimatedCommission)}</span>
              </div>
              <div className="flex justify-between pt-1 border-t border-border">
                <span className="font-medium text-text">Ajouté à votre solde</span>
                <span className="font-bold text-text">{fcfa(estimatedCredit)}</span>
              </div>
              <p className="text-xs text-text-secondary pt-1">
                Le montant exact est confirmé après la recharge.
              </p>
            </div>
          )}

          <p className="text-xs text-text-secondary">Paiement par Mobile Money.</p>

          <button type="submit" className="btn btn-primary btn-full" disabled={!isValid || submitting}>
            {submitting ? 'Recharge en cours...' : isValid ? `Recharger ${fcfa(value)}` : 'Recharger'}
          </button>
        </form>

        <div className="card">
          <h3 className="font-semibold text-text mb-3">Mes dernières recharges</h3>
          {!recharges.length ? (
            <p className="text-sm text-text-secondary text-center py-4">Aucune recharge pour le moment.</p>
          ) : (
            <div className="space-y-3">
              {recharges.map(tx => {
                const meta = (tx.metadata || {}) as { rechargeAmount?: number; commissionFcfa?: number };
                return (
                  <div key={tx.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-text">{fcfa(meta.rechargeAmount ?? tx.amountFcfa)}</p>
                      <p className="text-xs text-text-secondary">{formatDate(tx.createdAt)}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-600">+ {fcfa(tx.amountFcfa)}</p>
                      <p className="text-xs text-text-secondary">Commission : {fcfa(meta.commissionFcfa ?? 0)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
